import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
} from 'react';

import { createPortal } from 'react-dom';

import CaretRightIcon from '@/images/icons/ic_caret-right_n80_20px.svg';

import clsx from 'clsx';

import styles from './index.module.scss';

const scrollIntoViewWithOffset = (element: Element, offset: number) => {
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.scrollY - offset;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });
};

export interface CoachmarkStep {
  currentIndex: number;
  elements: string[];
  text: ReactNode;
  subtext?: string;
  position: 'top' | 'bottom' | 'auto';
  noOverlay?: boolean;
  noTooltip?: boolean;
  noNextButton?: boolean;
  onClick?: () => void;
}

interface CoachmarkProviderProps {
  children: ReactNode;
  onComplete?: () => void;
  totalSteps: number;
}

interface CoachmarkContextType {
  start: (steps: CoachmarkStep[]) => void;
  stop: () => void;
  setCurrentStep: (step: number) => void;
  totalSteps: number;
}

const CoachmarkContext = createContext<CoachmarkContextType | null>(null);

const useCoachmark = () => {
  const context = useContext(CoachmarkContext);
  if (!context) {
    throw new Error('useCoachmark must be used within CoachmarkProvider');
  }
  return context;
};

const Overlay = ({ highlightElements }: { highlightElements: string[] }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const elements = highlightElements.flatMap((selector) =>
      Array.from(document.querySelectorAll(selector)),
    );

    elements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.position = 'relative';
        el.style.zIndex = '1001';
      }
    });

    return () => {
      document.body.style.overflow = '';
      elements.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.position = '';
          el.style.zIndex = '';
          el.style.backgroundColor = '';
        }
      });
    };
  }, [highlightElements]);

  return <div className={styles.overlay} />;
};

interface TooltipProps {
  position: 'top' | 'bottom' | 'auto';
  elements: string[];
  text: string | ReactNode;
  subtext?: string;
  onNext: () => void;
  // onPrev: () => void;
  onComplete: () => void;
  currentStep: number;
  totalSteps: number;
  currentIndex: number;
  noNextButton?: boolean;
}

const Tooltip = ({
  text,
  subtext,
  position,
  onNext,
  // onPrev,
  onComplete,
  currentStep,
  totalSteps,
  currentIndex,
  elements,
  noNextButton = false,
}: TooltipProps) => {
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    const element = document.querySelector(elements[elements.length - 1]);
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const tooltipHeight = 200;
    const spaceBelow = viewportHeight - rect.bottom;

    if (position === 'auto') {
      const left = 20;
      const top =
        spaceBelow >= tooltipHeight + 16
          ? rect.bottom + window.scrollY + 16
          : rect.top + window.scrollY - tooltipHeight - 16;

      setTooltipPosition({ top, left });
    }
  }, [elements, position]);

  const tooltipClass =
    position === 'auto'
      ? styles.auto
      : position === 'top'
        ? styles.top
        : styles.bottom;

  return (
    <div
      className={`${styles.tooltip} ${tooltipClass}`}
      style={position === 'auto' ? tooltipPosition : undefined}
    >
      <p className={styles.text}>{text}</p>
      {subtext && <p className={styles.subtext}>{subtext}</p>}
      <div className={styles.footer}>
        <div className={styles.stepCounter}>
          {currentIndex}/{totalSteps}
        </div>
        {/* {currentStep > 0 && (
            <button onClick={onPrev} className={styles.prevButton}>
              이전
            </button>
          )} */}
        {!noNextButton &&
          (currentStep < totalSteps - 1 ? (
            <button
              onClick={onNext}
              className={clsx(styles.button, styles.nextButton)}
            >
              다음
              <CaretRightIcon />
            </button>
          ) : (
            <button
              onClick={onComplete}
              className={clsx(styles.button, styles.completeButton)}
            >
              완료
            </button>
          ))}
      </div>
    </div>
  );
};

interface CoachmarkProps {
  steps: CoachmarkStep[];
  totalSteps: number;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  isVisible: boolean;
  onComplete: () => void;
}

const Coachmark: React.FC<CoachmarkProps> = ({
  steps,
  totalSteps,
  currentStep,
  setCurrentStep,
  isVisible,
  onComplete,
}) => {
  useEffect(() => {
    if (!isVisible || !steps[currentStep]) {
      return;
    }

    const { elements } = steps[currentStep];
    const element = document.querySelector(elements[elements.length - 1]);
    if (!element) {
      return;
    }

    scrollIntoViewWithOffset(element, 100);
  }, [currentStep, steps, isVisible]);

  if (!isVisible || !steps[currentStep]) {
    return null;
  }

  const step = steps[currentStep];

  return createPortal(
    <>
      {!step.noOverlay && <Overlay highlightElements={step.elements} />}
      {!step.noTooltip && (
        <Tooltip
          {...step}
          elements={step.elements}
          currentStep={currentStep}
          currentIndex={step.currentIndex}
          totalSteps={totalSteps}
          onNext={() => {
            if (currentStep < steps.length - 1) {
              setCurrentStep(currentStep + 1);
            }
            step.onClick?.();
          }}
          // onPrev={() => {
          //   if (currentStep > 0) {
          //     setCurrentStep(currentStep - 1);
          //   }
          // }}
          onComplete={onComplete}
        />
      )}
    </>,
    document.body,
  );
};

export const CoachmarkProvider = ({
  children,
  onComplete,
  totalSteps,
}: CoachmarkProviderProps) => {
  const [steps, setSteps] = useState<CoachmarkStep[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const start = (newSteps: CoachmarkStep[]) => {
    setSteps(newSteps);
    setIsVisible(true);
  };

  const stop = () => {
    setSteps([]);
    setIsVisible(false);
    onComplete?.();
  };

  return (
    <CoachmarkContext.Provider
      value={{ start, stop, setCurrentStep, totalSteps }}
    >
      {children}
      <Coachmark
        steps={steps}
        totalSteps={totalSteps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        isVisible={isVisible}
        onComplete={stop}
      />
    </CoachmarkContext.Provider>
  );
};

export { useCoachmark };

export const withCoachmark = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  totalSteps: number,
) => {
  return (props: P) => {
    return (
      <CoachmarkProvider
        totalSteps={totalSteps}
        onComplete={() => console.log('Coachmark completed')}
      >
        <WrappedComponent {...props} />
      </CoachmarkProvider>
    );
  };
};
